/**
 * Shared plumbing for the controllers: the uniform error contract, the debounced project save
 * and the "these are the pieces every controller may reach for" dependency bundle.
 *
 * Not exported from `controllers/index.ts` beyond what the other controllers need — panels use
 * the `Controllers` surface only.
 */

import { AppError, isAppError } from '../../shared';
import type { CanonicalRigPlacement, SkinData } from '../../shared';
import type { ImportedModel } from '../../importExport';
import type { EditorContext } from '../context';
import type { ProjectMeta } from '../types';
import type { Controllers, RigController } from './types';

/** How long a burst of mapping / placement / skin edits is collected before it is persisted. */
export const SAVE_DEBOUNCE_MS = 300;

/**
 * The rig controller as the other controllers see it: the public `RigController` plus the pieces
 * the project controller needs for persistence and reload. Not part of the pinned contract, so it
 * stays internal to this directory.
 */
export interface InternalRigController extends RigController {
  /** Skin weights of every skinned mesh, in mesh order — what `ProjectRecord.skinData` stores. */
  allSkinData(): SkinData[];
  /**
   * Rebuild the SkinnedMesh(es) from a persisted placement + weights (project reopen, plan D21):
   * no re-computation of weights, no re-export.
   */
  rebuildFromPersisted(placement: CanonicalRigPlacement, skinData: readonly SkinData[]): void;
  /** Forget the cached meshes / skeleton / weights (new import, project closed). */
  resetInternal(): void;
  /** Drop this controller's store subscriptions. */
  dispose(): void;
}

export interface ControllerDeps {
  ctx: EditorContext;
  /**
   * The finished controller set. Assigned by `createControllers` immediately after construction,
   * so it is only ever read from inside a method call — never during a factory call.
   */
  controllers: Controllers;
  /** Same object as `controllers.rig`, with the internal extras visible. */
  rig: InternalRigController;
  /** Queue a debounced `project.saveProjectState()`. Safe to call from a store subscription. */
  scheduleSave(): void;
  /**
   * Persist right now, cancelling any pending debounce. For edits a refresh must not be able to
   * eat (skin painting, the end of a joint drag).
   */
  saveNow(): Promise<void>;
  /**
   * Run `state.rig.placement` writes WITHOUT the autosave subscription in `controllers/index.ts`
   * turning them into a database write.
   *
   * That subscription calls `saveNow()` the moment `placement` changes, which makes clearing the
   * placement on a project switch a *write*, not a store update — and a write attributed to the
   * project that is on its way out lands on its record as `placement: null` plus a deleted
   * `skinData` (the weights are gone from memory by then), i.e. a rigged project that reopens as
   * an unrigged blob. That is a worse failure than the cross-project leak this exists to fix, so
   * the suppression is EXPLICIT rather than a matter of which line comes first: moving a
   * statement inside `loadIntoSession` must not be able to re-open it.
   *
   * Reentrant (counted), and synchronous on purpose — store listeners fire synchronously, so the
   * whole reset finishes inside `run`.
   */
  suppressPlacementAutosave<T>(run: () => T): T;
  /**
   * Await any save still owed, cancelling the debounce — the same scheduler behind
   * `Controllers.flushPendingSaves()`, exposed to the controllers so a project switch can settle
   * the outgoing project's work before the incoming one becomes current (§2.1 #1 Path C).
   */
  flushSaves(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Error contract
// ---------------------------------------------------------------------------

/**
 * Errors already surfaced by an inner controller call. `importGlbFile` calls `mapping.autoMap()`,
 * which reports and rethrows; without this the user would see the same failure twice.
 */
const alreadyReported = new WeakSet<object>();

export function messageOf(error: unknown, fallback: string): string {
  if (isAppError(error)) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * The single error path every controller method uses: developer detail to the console, an
 * actionable message to the user (toast + the error log the UI can show), then rethrow so a caller
 * that cares can react. Panels are free to ignore the rejection (SPEC "Error Handling").
 */
export function report(ctx: EditorContext, error: unknown, fallback: string): never {
  const isObject = typeof error === 'object' && error !== null;
  if (isObject && alreadyReported.has(error)) throw error;
  if (isObject) alreadyReported.add(error);

  const message = messageOf(error, fallback);
  console.error(`[controllers] ${fallback}`, error);
  ctx.store.actions.toast('error', message);
  ctx.store.actions.pushError(message);
  throw error;
}

export async function guard<T>(
  ctx: EditorContext,
  fallback: string,
  run: () => Promise<T>,
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    return report(ctx, error, fallback);
  }
}

export function guardSync<T>(ctx: EditorContext, fallback: string, run: () => T): T {
  try {
    return run();
  } catch (error) {
    return report(ctx, error, fallback);
  }
}

// ---------------------------------------------------------------------------
// Preconditions
// ---------------------------------------------------------------------------

export function requireModel(ctx: EditorContext): ImportedModel {
  const model = ctx.session.model;
  if (!model) {
    throw new AppError('NO_MODEL', 'Import a model first — there is nothing loaded.');
  }
  return model;
}

export function requireProject(ctx: EditorContext): ProjectMeta {
  const project = ctx.store.getState().project;
  if (!project) {
    throw new AppError('NO_PROJECT', 'Import or open a project first.');
  }
  return project;
}

// ---------------------------------------------------------------------------
// Debounced save
// ---------------------------------------------------------------------------

/**
 * What a scheduled save BELONGS to — the project id that was open when it was scheduled.
 *
 * It has to travel with the save rather than be re-read when the timer fires: a 300 ms debounce
 * scheduled for project A fires after the user has already opened project B, and
 * `saveProjectState` used to read "the current project" at that moment and write A's store state
 * onto B's record (audit §2.1 #1, Path C). `null` means "no project was open"; `undefined` means
 * the caller is not making a claim (the unit tests of the scheduler itself).
 */
export type SaveScope = string | null | undefined;

export interface SaveScheduler {
  /** @param scope the project this save is for; handed back to `save` when the timer fires. */
  schedule(scope?: SaveScope): void;
  /** Run a pending save now (wave-4 `beforeunload`, tests). Resolves when the write finished. */
  flush(): Promise<void>;
  /**
   * Save immediately whether or not a save was pending, cancelling the debounce. For edits that
   * must survive a refresh that happens one keystroke later (weight painting, the end of a joint
   * drag) — `beforeunload` cannot await IndexedDB, so those writes cannot be left on a timer.
   */
  saveNow(scope?: SaveScope): Promise<void>;
  cancel(): void;
  /** True while a save is on the debounce timer or in flight. Drives `UiState.saving`. */
  isPending(): boolean;
}

export function createSaveScheduler(
  save: (scope: SaveScope) => Promise<void>,
  delay: number = SAVE_DEBOUNCE_MS,
  /**
   * Called whenever "is there unwritten work?" flips. `createControllers` forwards it to the
   * store so the toolbar can say so — `beforeunload` cannot await IndexedDB, so the user needs
   * a way to see that a write is still outstanding.
   */
  onPendingChange?: (pending: boolean) => void,
): SaveScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let reported = false;
  /** The scope of the save currently on the timer. Captured at schedule time, never re-read. */
  let pendingScope: SaveScope;

  const pending = (): boolean => timer !== null || inFlight !== null;

  /**
   * Deferred one microtask on purpose: `schedule()` is called from inside a store subscription,
   * and dispatching an action synchronously from there re-enters `notify()` mid-iteration.
   */
  const syncPending = (): void => {
    if (!onPendingChange) return;
    queueMicrotask(() => {
      const now = pending();
      if (now === reported) return;
      reported = now;
      onPendingChange(now);
    });
  };

  const run = (scope: SaveScope): Promise<void> => {
    // A failed autosave already toasted through `saveProjectState`; never turn it into an
    // unhandled rejection on a timer. Saves are chained rather than overlapped: two concurrent
    // read-modify-write cycles over the same record can otherwise land in the wrong order.
    // `scope` is the argument, not a re-read of "the current project": by the time this runs the
    // user may be in a different project entirely (§2.1 #1 Path C).
    const previous = inFlight ?? Promise.resolve();
    const next = previous.then(() => save(scope).catch(() => undefined));
    inFlight = next;
    syncPending();
    void next.then(() => {
      if (inFlight === next) inFlight = null;
      syncPending();
    });
    return next;
  };

  return {
    schedule(scope?: SaveScope): void {
      if (timer !== null) clearTimeout(timer);
      pendingScope = scope;
      const scheduledFor = scope;
      timer = setTimeout(() => {
        timer = null;
        void run(scheduledFor);
      }, delay);
      syncPending();
    },
    async flush(): Promise<void> {
      if (timer !== null) {
        const scheduledFor = pendingScope;
        clearTimeout(timer);
        timer = null;
        await run(scheduledFor);
      }
      if (inFlight) await inFlight;
      syncPending();
    },
    saveNow(scope?: SaveScope): Promise<void> {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      return run(scope);
    },
    cancel(): void {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      syncPending();
    },
    isPending: pending,
  };
}
